import { useEffect, useRef, useState } from "react";
import { IntroReadyPanel } from "./Intro";
import { SpaceBackground } from "./SpaceBackground";
import { StormLayer } from "./StormLayer";

const LINES = [
  "$CURB BIOS v1.0",
  "COPYRIGHT (C) SOLANA BULL SYSTEMS",
  "",
  "INITIALIZING CHAIN...................OK",
  "CONNECTING TO SOLANA MAINNET.........OK",
  "LOADING BULL PROTOCOL................OK",
  "CHECKING LIQUIDITY POOLS..............OK",
  "SCANNING BLOCKCHAIN FOR THREATS.......CLEAR",
  "SCANNING FOR PAPER HANDS..............NONE FOUND",
  "CALIBRATING HORNS.....................100%",
  "CHARGING LIGHTNING RESERVES...........FULL",
  "VERIFYING DIAMOND HANDS................CONFIRMED",
  "BULL AGGRESSION LEVEL..................MAXIMUM",
  "",
  "ALL SYSTEMS NOMINAL",
];
const CHARGE_LINE = "> $CURB IS READY TO CHARGE";
const TYPING_TOTAL_MS = 15000;
const BOOT_TRIGGER_S = 21.1;
const TOTAL_TICKS = LINES.reduce((n, l) => n + l.length, 0) + LINES.length;
const TICK_MS = TYPING_TOTAL_MS / TOTAL_TICKS;

const lineColor = (line) =>
  line === "ALL SYSTEMS NOMINAL" ? "#14F195" : line.startsWith("$CURB") || line.startsWith("COPYRIGHT") ? "#a78bfa" : "#d4d4d8";

// timestamped audio debug log
const alog = (msg) => console.log(`[intro-audio +${performance.now().toFixed(0)}ms] ${msg}`);

export const MobileBoot = ({ onFinished, codingRef, successRef }) => {
  // stage: typing | hold | charge | bar
  const [stage, setStage] = useState("typing");
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [chargeChars, setChargeChars] = useState(0);
  const [barPct, setBarPct] = useState(-1);
  const holdFired = useRef(false);

  useEffect(() => {
    if (stage !== "typing") return;
    if (lineIdx >= LINES.length) {
      if (!holdFired.current) {
        holdFired.current = true;
        const coding = codingRef.current;
        if (coding) {
          alog("coding: pause() (end of typing)");
          coding.pause();
          coding.currentTime = 0;
        }
        const success = successRef.current;
        if (success) {
          alog("success: volume=0.45 (real trigger)");
          success.volume = 0.45;
          success.currentTime = 0;
          alog("success: play() (real trigger)");
          success.play().catch((e) => console.error("Success Noise play() failed:", e));
        }
        setStage("hold");
      }
      return;
    }
    const line = LINES[lineIdx];
    const t = setTimeout(() => {
      if (charIdx < line.length) setCharIdx((c) => c + 1);
      else {
        setLineIdx((l) => l + 1);
        setCharIdx(0);
      }
    }, TICK_MS);
    return () => clearTimeout(t);
  }, [stage, lineIdx, charIdx]);

  useEffect(() => {
    if (stage !== "hold") return;
    const t = setTimeout(() => setStage("charge"), 2050);
    return () => clearTimeout(t);
  }, [stage]);

  useEffect(() => {
    if (stage !== "charge") return;
    if (chargeChars >= CHARGE_LINE.length) {
      setStage("bar");
      setBarPct(0);
      return;
    }
    const t = setTimeout(() => setChargeChars((c) => c + 1), 35);
    return () => clearTimeout(t);
  }, [stage, chargeChars]);

  useEffect(() => {
    if (stage !== "bar" || barPct < 0) return;
    if (barPct >= 100) {
      const t = setTimeout(onFinished, 250);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setBarPct((p) => Math.min(100, p + 2)), 70);
    return () => clearTimeout(t);
  }, [stage, barPct, onFinished]);

  return (
    <div className="w-full max-w-2xl px-5" data-testid="intro-boot-log">
      <pre className="font-mono whitespace-pre-wrap leading-relaxed" style={{ fontSize: "clamp(0.6rem, 2.7vw, 0.85rem)" }}>
        {LINES.slice(0, lineIdx).map((l, i) => (
          <div key={i} style={{ color: lineColor(l) }}>{l === "" ? "\u00A0" : l}</div>
        ))}
        {stage === "typing" && lineIdx < LINES.length && (
          <div style={{ color: lineColor(LINES[lineIdx]) }}>
            {LINES[lineIdx].slice(0, charIdx)}
            <span className="intro-cursor">█</span>
          </div>
        )}
        {(stage === "charge" || stage === "bar") && (
          <div style={{ color: "#14F195" }} data-testid="intro-charge-line">
            {CHARGE_LINE.slice(0, chargeChars)}
            {stage === "charge" && <span className="intro-cursor">█</span>}
          </div>
        )}
      </pre>

      {barPct >= 0 && (
        <div className="mt-6" data-testid="intro-loading-bar">
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden border border-white/10">
            <div
              className="h-full rounded-full"
              style={{
                width: `${barPct}%`,
                background: "linear-gradient(90deg, #9945FF 0%, #5497D5 50%, #14F195 100%)",
                boxShadow: "0 0 14px rgba(20,241,149,0.6)",
                transition: "width 40ms linear",
              }}
            />
          </div>
          <p className="font-mono text-xs text-zinc-500 mt-2 text-right">{barPct}%</p>
        </div>
      )}
    </div>
  );
};

export const IntroMobile = ({ onEnter, stormLevel = 0 }) => {
  // phases: video | boot | ready
  const [phase, setPhase] = useState("video");
  const [needsTap, setNeedsTap] = useState(false);
  const [tapExiting, setTapExiting] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const videoRef = useRef(null);
  const codingRef = useRef(null);
  const successRef = useRef(null);
  const bootStarted = useRef(false);
  const debugTap = useRef(
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tapdebug") === "1"
  );

  const startBoot = () => {
    if (bootStarted.current) return;
    bootStarted.current = true;
    setNeedsTap(false);
    const coding = codingRef.current;
    if (coding) {
      alog("coding: volume=0.45 (real trigger 21.10s)");
      coding.volume = 0.45;
      coding.currentTime = 0;
      alog("coding: play() (real trigger 21.10s)");
      coding.play().catch((e) => console.error("Coding Noise play() failed:", e));
    } else {
      console.error("Coding Noise audio element missing at boot trigger");
    }
    setPhase("boot");
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (debugTap.current) {
      setNeedsTap(true);
      return;
    }
    const p = v.play();
    if (p && p.catch) p.catch(() => setNeedsTap(true));
  }, []);

  // silent per-element unlock — MUST be fully synchronous inside the tap gesture
  const tapToStart = () => {
    const dismissTap = () => {
      setTapExiting(true);
      setTimeout(() => {
        setNeedsTap(false);
        setTapExiting(false);
      }, 400);
    };
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      alog("video.play() (tap handler)");
      const p = v.play();
      if (p && p.then)
        p.then(() => dismissTap()).catch(() => {
          dismissTap();
          startBoot();
        });
    }
    [
      ["coding", codingRef.current],
      ["success", successRef.current],
    ].forEach(([name, a]) => {
      if (!a) return;
      alog(`${name}: volume=0 (unlock)`);
      a.volume = 0;
      alog(`${name}: play() (unlock)`);
      const ap = a.play();
      if (ap && ap.catch) ap.catch((e) => alog(`${name}: unlock play() rejected: ${e && e.name}`));
      alog(`${name}: pause() (unlock)`);
      a.pause();
      a.currentTime = 0;
      alog(`${name}: currentTime=0 (unlock done)`);
    });
  };

  const handleEnter = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => onEnter(), 1000);
  };

  const skipIntro = () => {
    if (skipping || phase === "ready") return;
    setSkipping(true);
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.muted = true;
    }
    [codingRef.current, successRef.current].forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
    bootStarted.current = true;
    setTimeout(() => {
      setPhase("ready");
      setSkipping(false);
    }, 350);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black overflow-hidden"
      style={{ height: "100dvh" }}
      data-testid="intro-screen"
    >
      <audio ref={codingRef} src="/assets/audio/coding-noise-v2.mp3" preload="auto" loop data-testid="intro-coding-audio" />
      <audio ref={successRef} src="/assets/audio/success-noise.mp3" preload="auto" data-testid="intro-success-audio" />
      <video
        ref={videoRef}
        src="/assets/videos/intro-mobile-v2.mp4"
        autoPlay
        playsInline
        preload="auto"
        onTimeUpdate={(e) => {
          if (e.target.currentTime >= BOOT_TRIGGER_S) startBoot();
        }}
        onEnded={startBoot}
        onError={() => {
          if (!debugTap.current) startBoot();
        }}
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="intro-video"
      />

      {needsTap && phase === "video" && (
        <button
          onClick={tapToStart}
          data-testid="intro-tap-to-start"
          className="tap-press absolute inset-0 z-10 w-full bg-black"
          style={{ opacity: tapExiting ? 0 : 1, transition: "opacity 0.4s ease", pointerEvents: tapExiting ? "none" : "auto" }}
        >
          <div className="relative h-full flex flex-col items-center justify-center">
            <span
              className="font-bebas text-white tracking-[0.45em] pl-[0.45em]"
              style={{
                fontSize: "clamp(1.05rem, 4.5vw, 1.5rem)",
                textShadow: "0 0 22px rgba(153,69,255,0.85), 0 0 44px rgba(153,69,255,0.4)",
              }}
            >
              TAP TO ENTER THE STORM
            </span>
          </div>
        </button>
      )}

      {phase !== "ready" && !needsTap && (
        <button
          onClick={skipIntro}
          data-testid="intro-skip-button"
          className="skip-intro-btn font-bebas tracking-[0.25em] text-sm"
        >
          SKIP INTRO
        </button>
      )}

      {phase !== "video" && (
        <div className="intro-fadein absolute inset-0" data-testid="intro-mobile-storm-bg">
          <SpaceBackground />
          <StormLayer level={Math.max(stormLevel, 2)} />
        </div>
      )}

      {phase === "boot" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center intro-fadein">
          <MobileBoot onFinished={() => setPhase("ready")} codingRef={codingRef} successRef={successRef} />
        </div>
      )}

      {phase === "ready" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <IntroReadyPanel onEnter={handleEnter} />
        </div>
      )}

      <div
        className="absolute inset-0 z-20 bg-black pointer-events-none"
        style={{ opacity: skipping ? 1 : 0, transition: "opacity 0.35s ease" }}
      />
      <div
        className="absolute inset-0 z-20 bg-black pointer-events-none"
        style={{ opacity: exiting ? 1 : 0, transition: "opacity 1s ease" }}
        data-testid="intro-exit-fade"
      />
    </div>
  );
};
